// Billing Domain Types for NLPask Subscription & Credits System

// ============================================================================
// Common Types
// ============================================================================

export interface PagedResult<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

// ============================================================================
// Enums
// ============================================================================

export type SubscriptionStatus = 'Trialing' | 'Active' | 'PastDue' | 'Cancelled' | 'Expired';

export type CreditTransactionType =
  | 'Subscription'
  | 'Purchase'
  | 'Usage'
  | 'Refund'
  | 'Bonus'
  | 'Expired'
  | 'Adjustment';

export type CreditActionType =
  | 'CreateSurvey'
  | 'CreateExam'
  | 'CreateQuiz'
  | 'CreateAssessment'
  | 'AddQuestion'
  | 'ReceiveResponse'
  | 'AiAnalysis'
  | 'ExportData';

export type PaymentStatus = 'Pending' | 'Completed' | 'Failed' | 'Refunded' | 'Cancelled';

// ============================================================================
// Subscription Plans
// ============================================================================

export interface SubscriptionPlan {
  id: number;
  code: string;
  name: string;
  description?: string;
  monthlyPriceCents: number;
  annualPriceCents?: number;
  monthlyCredits: number;
  // Limits (null = unlimited)
  maxSurveys?: number;
  maxQuestionsPerSurvey?: number;
  maxResponsesPerSurvey?: number;
  maxExams?: number;
  maxQuizzes?: number;
  maxAssessments?: number;
  // Metadata
  features?: Record<string, unknown>;
  displayOrder: number;
  isActive: boolean;
}

export interface UserSubscription {
  id: number;
  userId: number;
  planId: number;
  plan?: SubscriptionPlan;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  cancelledAt?: string;
  trialEnd?: string;
  createdDate: string;
}

// ============================================================================
// Credits
// ============================================================================

export interface CreditBalance {
  userId: number;
  subscriptionCredits: number;
  subscriptionCreditsExpireAt?: string;
  purchasedCredits: number;
  purchasedCreditsExpireAt?: string;
  bonusCredits: number;
  bonusCreditsExpireAt?: string;
  totalCredits: number;
  lastResetDate?: string;
}

export interface CreditTransaction {
  id: number;
  userId: number;
  transactionType: CreditTransactionType;
  actionType?: CreditActionType;
  amount: number;
  balanceAfter: number;
  description?: string;
  referenceType?: string;
  referenceId?: number;
  createdDate: string;
}

export interface CreditPack {
  id: number;
  code: string;
  name: string;
  description?: string;
  priceCents: number;
  credits: number;
  bonusCredits: number;
  totalCredits: number;
  displayOrder: number;
  isActive: boolean;
}

export interface CreditCosts {
  createSurvey: number;
  createExam: number;
  createQuiz: number;
  createAssessment: number;
  addQuestion: number;
  receiveResponse: number;
  aiAnalysis: number;
  exportData: number;
}

// ============================================================================
// Invoices
// ============================================================================

export interface Invoice {
  id: number;
  userId: number;
  externalInvoiceId?: string;
  amountCents: number;
  currency: string;
  status: PaymentStatus;
  description?: string;
  invoicePdfUrl?: string;
  paidAt?: string;
  purchaseType?: string;
  purchaseId?: number;
  createdDate: string;
}

// ============================================================================
// API Request/Response DTOs
// ============================================================================

export interface SubscribeDto {
  planCode: string;
  annual: boolean;
  cardNumber: string;
}

export interface PurchaseCreditsDto {
  packCode: string;
  cardNumber: string;
}

export interface SubscriptionResult {
  success: boolean;
  message?: string;
  subscription?: UserSubscription;
}

export interface CreditPurchaseResult {
  success: boolean;
  message?: string;
  creditsAdded?: number;
  balance?: CreditBalance;
  invoice?: Invoice;
}

// ============================================================================
// Utility Types
// ============================================================================

export interface PlanLimits {
  maxSurveys?: number;
  maxQuestionsPerSurvey?: number;
  maxResponsesPerSurvey?: number;
  maxExams?: number;
  maxQuizzes?: number;
  maxAssessments?: number;
}

export interface CreditUsageSummary {
  totalSpent: number;
  periodStart: string;
  periodEnd: string;
  byAction: Record<CreditActionType, number>;
}

// Helper to format price from cents
export const formatPrice = (cents: number | undefined | null, currency = 'USD'): string => {
  // Handle null/undefined/NaN
  if (cents == null || isNaN(cents)) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(0);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
};

// Helper to check if a plan has a specific limit
export const hasLimit = (value: number | null | undefined): boolean => {
  return value !== null && value !== undefined;
};

// Credit cost constants (should match backend)
export const CREDIT_COSTS: CreditCosts = {
  createSurvey: 5,
  createExam: 10,
  createQuiz: 5,
  createAssessment: 10,
  addQuestion: 1,
  receiveResponse: 2,
  aiAnalysis: 5,
  exportData: 3,
};
